import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LookupService, CommonlyUsedEmail, SaveCommonlyUsedEmailRequest, UserSearchResult } from '../../services/lookup.service';
import { LoggedUserService } from '../../services/logged-user.service';
import { MaterialModule } from '../../material.module';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

export interface UserSearchDialogData {
  fieldLabel: string;
}

export interface UserSearchDialogResult {
  name: string;
  email: string;
}

@Component({
  selector: 'app-user-search-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './user-search-dialog.component.html',
  styleUrls: ['./user-search-dialog.component.css'],
})
export class UserSearchDialogComponent implements OnInit {
  commonlyUsed: CommonlyUsedEmail[] = [];
  searchResults: UserSearchResult[] = [];
  firstName = '';
  lastName = '';
  isSearching = false;
  isLoadingCommon = true;
  isEditMode = false;
  isAdding = false;
  isSavingCommon = false;
  editingId: number | null = null;
  commonEmail = '';
  commonName = '';

  constructor(
    public dialogRef: MatDialogRef<UserSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserSearchDialogData,
    private lookupService: LookupService,
    private loggedUserService: LoggedUserService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.lookupService.getCommonlyUsedEmails().subscribe({
      next: (list) => {
        this.commonlyUsed = list;
        this.isLoadingCommon = false;
      },
      error: () => {
        this.isLoadingCommon = false;
      },
    });
  }

  onSearch(): void {
    if (!this.firstName && !this.lastName) return;
    this.isSearching = true;
    this.lookupService.searchUsers(this.firstName, this.lastName).subscribe({
      next: (resp) => {
        this.searchResults = resp.value;
        this.isSearching = false;
      },
      error: () => {
        this.isSearching = false;
      },
    });
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    this.cancelCommonEdit();
  }

  startEdit(item: CommonlyUsedEmail): void {
    this.editingId = item.id;
    this.isAdding = false;
    this.commonEmail = item.email;
    this.commonName = item.name;
  }

  startAdd(): void {
    this.editingId = null;
    this.isAdding = true;
    this.commonEmail = '';
    this.commonName = '';
  }

  cancelCommonEdit(): void {
    this.editingId = null;
    this.isAdding = false;
    this.commonEmail = '';
    this.commonName = '';
  }

  saveCommonUser(): void {
    if (!this.isCommonUserValid() || this.isSavingCommon) return;

    const request: SaveCommonlyUsedEmailRequest = {
      email: this.commonEmail.trim(),
      name: this.commonName.trim(),
    };
    const operation = this.isAdding
      ? this.lookupService.createCommonlyUsedEmail(request)
      : this.lookupService.updateCommonlyUsedEmail(this.editingId!, request);

    this.isSavingCommon = true;
    operation.subscribe({
      next: (saved) => {
        const index = this.commonlyUsed.findIndex(item => item.id === saved.id);
        this.commonlyUsed = index >= 0
          ? this.commonlyUsed.map(item => item.id === saved.id ? saved : item)
          : [...this.commonlyUsed, saved];
        this.commonlyUsed.sort((a, b) => a.name.localeCompare(b.name));
        this.isSavingCommon = false;
        this.cancelCommonEdit();
        this.snackBar.open('Commonly used user saved.', 'Close', { duration: 3000 });
      },
      error: (error) => {
        this.isSavingCommon = false;
        this.snackBar.open(error?.error?.detail || 'Unable to save the commonly used user.', 'Close', { duration: 5000 });
      },
    });
  }

  deleteCommonUser(item: CommonlyUsedEmail): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove commonly used user?',
        message: `${item.name} (${item.email}) will be removed from this list.`,
        confirmLabel: 'Remove',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
    }).afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.lookupService.deleteCommonlyUsedEmail(item.id).subscribe({
        next: () => {
          this.commonlyUsed = this.commonlyUsed.filter(user => user.id !== item.id);
          if (this.editingId === item.id) this.cancelCommonEdit();
          this.snackBar.open('Commonly used user removed.', 'Close', { duration: 3000 });
        },
        error: () => this.snackBar.open('Unable to remove the commonly used user.', 'Close', { duration: 5000 }),
      });
    });
  }

  isCommonUserValid(): boolean {
    const email = this.commonEmail.trim();
    return this.commonName.trim().length > 0 && this.commonName.trim().length <= 50 &&
      email.length <= 256 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  onSelectCommon(item: CommonlyUsedEmail): void {
    if (!this.isEditMode) {
      this.dialogRef.close({ name: item.name, email: item.email } as UserSearchDialogResult);
    }
  }

  onSelectResult(item: UserSearchResult): void {
    const email = item.mail || item.userPrincipalName;
    const name = item.displayName;
    this.dialogRef.close({ name, email } as UserSearchDialogResult);
  }

  onUseCurrentUser(): void {
    const email = this.loggedUserService.getCurrentEmail();
    if (email) {
      this.dialogRef.close({ name: email, email } as UserSearchDialogResult);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
